import { useCallback } from 'react';
import { normalizeDegrees } from '@alltrails/data-formatting/cartographyConverters';
import type BoundingBox from '@alltrails/explore-service/types/BoundingBox';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AskLocation } from '@/utils/askAllTrailsApi';

// Across the dateline the map reports longitudes past 180; the server expects -180 to 180.
const wrap = ({ lat, lng }: BoundingBox['topRight']) => ({ lat, lng: lng >= -180 && lng <= 180 ? lng : normalizeDegrees(lng + 180) - 180 });

/**
 * Returns a function that reads the part of the map the user can see, as the corner pair Ask AllTrails accepts. Call it when
 * the question is submitted, so the location is a copy and panning never changes a request.
 */
const useAskLocation = () => {
  const map = useMap();

  return useCallback((): AskLocation | undefined => {
    if (!map) {
      return undefined;
    }

    const { bottomLeft, topRight } = getMapBoundingBox(map);
    const lngSpan = Math.abs(topRight.lng - bottomLeft.lng);

    // No area means the map hasn't measured itself yet, and a view wider than the world isn't a search area; either way
    // the question has to name a place instead
    if (lngSpan === 0 || lngSpan >= 360) {
      return undefined;
    }

    return { topRight: wrap(topRight), bottomLeft: wrap(bottomLeft) };
  }, [map]);
};

export default useAskLocation;
